/* eslint-disable camelcase */
import { FormikProps, FormikValues } from "formik"
import { observer } from "mobx-react-lite"
import moment from "moment"
import React, {FC, useCallback, useEffect, useMemo, useRef, useState, memo } from "react"
import { Alert, Appearance, Platform, ScrollView, ViewStyle } from "react-native"
import { Button, FeedingTrayControlForm, JalaLoading, Screen, useRouteName } from "../../../../components"
import { useStores } from "../../../../models"
import { goBack} from "../../../../navigators"
import { color, nativeBaseTheme } from "../../../../theme"
import SyncableModel from "../../../../database/SyncableModel";
import { Cycle, FeedingTrayControl } from "../../../../database";
import { CompositeScreenProps, useIsFocused } from "@react-navigation/native";
import { Modal, View } from "native-base"
import _ from "lodash"
import { exitWalkthrough, startWalkthrough } from "react-native-walkthrough"
import formWalkthrough from "../../../../guides/form-walkthrough";
import { translate } from "../../../../i18n";
import JalaAnalytic from "../../../../utils/jala-analytic"

const ROOT: ViewStyle = {
  backgroundColor: color.palette.white,
  flexGrow: 1,
}

const SPACE: ViewStyle = {
  flex: 1,
}

const BUTTON_CONTAINER: ViewStyle = {
  borderTopWidth: 1,
  borderTopColor: color.palette.gray300,
}

const DELETE_BUTTON: ViewStyle = {
  alignSelf: "flex-end",
  marginVertical: 8,
  marginHorizontal: 16
}

const BUTTON: ViewStyle = {
  marginVertical: 8,
  marginHorizontal: 16
}

const FormWalkthrough: FC<{
  scrollViewRef?: { current: ScrollView }
}> = memo(observer(function FormWalkthrough(props) {
  const { scrollViewRef } = props
  const { userStore } = useStores()
  const { user } = userStore
  const isFocused = useIsFocused()

  useEffect(() => {
    if (!isFocused || !user || user?.state?.add_feed_tray_coachmark_shown) return undefined
    startWalkthrough(formWalkthrough(
      [
        {
          id: 'feeding-tray-form-first',
          content: translate("feedingTrayControlForm.trayTooltip"),
          placement: 'top',
          tooltipProps: {},
        },
        {
          id: 'feeding-tray-form-second',
          content: translate("feedingTrayControlForm.remainFeed"),
          placement: 'top',
          tooltipProps: {},
        },
      ], () => {
        userStore.updateData({ state: { add_feed_tray_coachmark_shown: true } })
          .finally(() => userStore.getUser())
        if (scrollViewRef && scrollViewRef.current)
          return scrollViewRef.current.scrollTo({ y: 0 })
      },
    ))
    return () => isFocused && exitWalkthrough();
  }, [isFocused, scrollViewRef, user, userStore])

  return null
}))

export const FeedingTrayCreateMultipleScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function FeedingTrayCreateMultipleScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), route: { params = {} } } = props

  const [ isError, setIsError ] = useState<boolean>(true);
  const [ model, setModel ] = useState<SyncableModel>(null);
  const [ feedingTrayControls, setFeedingTrayControls] = useState<any>([]);

  const { routeNameRef, previousRouteNameRef } = useRouteName()

  const scrollViewRef = useRef<ScrollView>(null);

  // Pull in one of our MST stores
  const {
    appStore: { farmId, farmStore },
    feedingTrayControlStore: modelStore,
    feedingTrayControlStore: { loading, error: modelError },
  } = useStores()

  const modelForm = useRef<FormikProps<FormikValues>>()
  const modelFormRef = useCallback((node) => {
    modelForm.current = node;
    if (modelForm.current) setIsError(!(modelForm.current.dirty && modelForm.current.isValid))
  }, [])

  const initialValues = useMemo<any>(() => {
    const data = (model ? model.toArray() : params)
    return {
      farm_id: farmId,
      ...data,
      logged_at: data.logged_at && moment.utc(data.logged_at).format('YYYY-MM-DD HH:mm:ss'),
      logged_date: data.logged_at && moment.utc(data.logged_at).format('YYYY-MM-DD'),
      logged_time: data.logged_at && moment.utc(data.logged_at).format('HH:mm:ss'),
      feeding_tray_controls: params.feeding_tray_controls || feedingTrayControls
    }
  }, [farmId, feedingTrayControls, model, params])

  useEffect(() => {
    if (params && params.cycle_id) {
      Cycle.fetch({ id__eq: params.cycle_id }).then((response) => {
        const cycle = response.length && response[0]
        const feedingTrayControls =  _.map(
          Array(cycle.feeding_tray_count || 0),
          (v, index) => ({ tray_number: index + 1, feed_remaining_percent: null }))
        setFeedingTrayControls(feedingTrayControls)
      })
    }
  }, [params, params?.cycle_id])

  useEffect(() => {
    const feedingTrayControl = farmStore.feedingTrayControls.find((m) => m.id === params.id);
    feedingTrayControl && setModel(FeedingTrayControl.hydrate({ ...feedingTrayControl }))
  }, [farmStore, params])

  useEffect(() => {
    if (!modelStore) return;
    if (modelError) {
      if (modelError.response && modelError.response.status === 422) {
        const errors = {}
        Object.keys(modelError.response.data.errors).forEach((key) => {
          errors[key] = (modelError.response.data.errors[key] || [])[0]
        })
        if (modelForm.current) modelForm.current.setErrors(errors)
        modelStore.set('error', null)
      }
      else {
        if (Platform.OS === 'web') {
          if (modelError.response)
            confirm(modelError.response.originalError.message)
          modelStore.set('error', null)
        }
        else
          Alert.alert(
            'Error',
            modelError.message,
            [ { onPress: () => modelStore.set('error', null) } ]
          );
      }
    }
  }, [modelError, modelStore])

  const onSubmit = useCallback(async (data) => {
    modelStore.set('loading', true)
    let success;
    if (!model) {
      const multipleData = (data.feeding_tray_controls || [])
        .filter((f) => f.feed_remaining_percent != null)
        .map((f) => ({
          logged_at: data.logged_at,
          feed_logged_at: data.feed_logged_at,
          cycle_id: data.cycle_id,
          remark: data.remark,
          ...f,
          feeding_tray_controls: undefined
        }))
      success = await modelStore.storeMultiple(multipleData)
    } else {
      const multipleData = (data.feeding_tray_controls || [])
        .filter((f) => f.feed_remaining_percent != null)
        .map((f) => {
          const feedingTrayControl = farmStore.feedingTrayControls.find((m) => m.id === f.id)
          const model = feedingTrayControl && FeedingTrayControl.hydrate(feedingTrayControl)
          return {
            model,
            data: {
              logged_at: data.logged_at,
              feed_logged_at: data.feed_logged_at,
              cycle_id: data.cycle_id,
              remark: data.remark,
              ...f,
              feeding_tray_controls: undefined
            }
          }
        })
      success = await modelStore.updateMultiple(multipleData)
    }

    JalaAnalytic.log("Mobile app: Form On Submit", {
      form: FeedingTrayControlForm.displayName || 'Unknown',
      data: JSON.stringify(data || null),
      success,
      routeName: routeNameRef.current,
      previousRouteName: previousRouteNameRef.current
    })

    if (success) {
      modelStore.set('needRefresh', true)
      goBack('Home.Stack')
    }
    modelStore.set('loading', false);
  }, [modelStore, model, routeNameRef, previousRouteNameRef, farmStore.feedingTrayControls])

  const handleSubmit = useCallback((event) => {
    if (modelForm.current) modelForm.current.handleSubmit(event)
  }, [])

  const handleDelete = useCallback(async () => {
    modelStore.set('loading', true)
    const multipleModels = (initialValues.feeding_tray_controls || []).map((f) => {
      const feedingTrayControl = farmStore.feedingTrayControls.find((m) => m.id === f.id)
      return feedingTrayControl && FeedingTrayControl.hydrate({ ...feedingTrayControl })
    }).filter((m) => m)
    const success = await modelStore.deleteMultiple(multipleModels)
    if (success) {
      modelStore.set('needRefresh', true)
      goBack('Home.Stack')
    }
    modelStore.set('loading', false)
  }, [farmStore.feedingTrayControls, modelStore, initialValues.feeding_tray_controls])

  return (<>
    <Screen
      style={ROOT}
      preset="scroll"
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
      scrollViewRef={scrollViewRef}
    >
      <FeedingTrayControlForm
        colorScheme={colorScheme}
        initialValues={initialValues}
        innerRef={modelFormRef}
        onSubmit={onSubmit}
      />
      <View style={SPACE} />
      <View style={BUTTON_CONTAINER}>
        {model && <Button
          testID="jala:id/delete-button"
          tx="delete"
          textPreset="title4"
          preset="link"
          colorScheme={colorScheme}
          textColor={loading ? nativeBaseTheme.colors.muted["500"] : nativeBaseTheme.colors.danger["500"]}
          disabled={loading}
          onPress={handleDelete}
          style={DELETE_BUTTON}
        />}
        <Button
          testID="jala:id/save-button"
          tx="save"
          colorScheme={colorScheme}
          disabled={isError}
          loading={loading}
          onPress={handleSubmit}
          style={BUTTON}
        />
      </View>
      <Modal isOpen={loading}>
        <View
          width="72px" height="72px"
          alignItems="center"
          justifyContent="center"
          rounded="48px"
          backgroundColor="rgba(255, 255, 255, 0.75)"
        >
          <JalaLoading />
        </View>
      </Modal>
    </Screen>
    <FormWalkthrough scrollViewRef={scrollViewRef} />
  </>)
})
